/** @file This file implements the side bar. */
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import * as icons from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import CgLogo from '@/components/CgLogo';
import { getDocumentationLink } from '@/components/HelpHomepageLink';
import { Icon } from '@/components/Icon';
import * as models from '@/models';
import * as store from '@/store';
import * as utils from '@/utils';
import { SidebarButton } from './components/SidebarButton';
import ToggleStudentViewButton from './components/ToggleStudentViewButton';
import UserInfo from './UserInfo';
const Sidebar = tsx.component({
    name: 'app-sidebar',
    /** @returns The data for this component. */
    data() {
        return { panelOpen: false };
    },
    computed: {
        /* Section: Entities */
        /** @returns Potentially the id of the course we are currently viewing. */
        courseId() {
            return this.$routeParamAsId('courseId');
        },
        /** @returns Potentially the id of the assignment we are currently viewing. */
        assignmentId() {
            return this.$routeParamAsId('assignmentId');
        },
        /** @returns The current user in the User Store. */
        loggedInUser() {
            return store.UserStore.loggedInUser();
        },
        /* Section: Conditions for buttons to show/hide */
        /** @returns If the user can login. */
        canLogIn() {
            return this.loggedInUser.isNothing();
        },
        /** @returns If the user can logout. */
        hasUser() {
            return this.loggedInUser.isJust();
        },
        /** @returns If the context allows new user registration. */
        canRegister() {
            if (this.hasUser) {
                return false;
            }
            return store.SiteSettingsStore.getSetting()('REGISTER_ENABLED', Maybe.empty());
        },
        /** @returns Whether we are currently in student view. */
        isStudentViewEnabled() {
            return store.UserStore.studentView().tag === 'enabled';
        },
        /** @returns Whether we want to hide the normal sidebar, and show a minimal simple variant. */
        simpleSidebar() {
            return this.$inLTI || this.isStudentViewEnabled;
        },
        /** @returns Can we manage the current LTI assignment. */
        canManageCurrentLtiAssignment() {
            if (this.hasUser && this.$LTICourseId != null && this.$inLTI) {
                return store.CoursesStore.getCourse()(this.$LTICourseId)
                    .filter(x => x instanceof models.Course)
                    .mapOrDefault(course => course.canManage, false);
            }
            return false;
        },
        /* Section: UI Content */
        /** @returns The label for the user. */
        userLabel() {
            //
            let label = this.loggedInUser
                .map(u => u.name.orDefault('Your Profile'))
                .orDefault(utils.ELLIPSIS);
            // Add student view
            if (store.UserStore.studentView().tag === 'enabled') {
                label = `${label} (student view)`;
            }
            return label;
        },
        /* Section: Sidebar Buttons */
        /** @returns Potentially the button for the current user. */
        adminPanelButton() {
            const user = this.loggedInUser.extract();
            if (user == null) {
                return utils.emptyVNode();
            }
            if (store.UserStore.studentView().tag === 'enabled') {
                return utils.emptyVNode();
            }
            let action;
            if (store.UserStore.canManageSite()) {
                const isActive = this.$route.name === 'admin';
                action = { tag: 'link', location: { name: 'admin' }, isActive };
            }
            else {
                const maybeTenantId = user.tenantId.filter(tid => store.UserStore.canManageTenant()(Maybe.of(tid)));
                if (maybeTenantId.isNothing()) {
                    return utils.emptyVNode();
                }
                else {
                    const tenantId = maybeTenantId.value;
                    action = {
                        tag: 'link',
                        location: { name: 'tenant', params: { tenantId } },
                        isActive: this.$route.name === 'tenant' &&
                            this.$route.params.tenantId === tenantId,
                    };
                }
            }
            return <SidebarButton icon={icons.AdminPanel} label="Admin" type={action}/>;
        },
        /** @returns The node for the expanded panel. */
        panelNode() {
            if (this.panelOpen === false) {
                return utils.emptyVNode();
            }
            return (<aside class={[
                    'fixed left-12 inset-y-0 h-screen shadow-lg m-0',
                    'w-82 sm:w-110',
                    'p-2 sm:p-4',
                    'bg-white/100 dark:bg-neutral-70',
                    'overflow-auto',
                    'z-(--z-modal)',
                ]} data-cy="panel-user-profile">
                    <header class="flex items-center justify-between gap-2 mb-2 sm:mb-3">
                        <h2 class="title-medium mb-0 dark:text-brand-10">User Info</h2>
                        <button type="button" class="cg-btn btn-neutral-1 size-8" onClick={this.closePanel} data-cy="btn-close-sidebar-panel">
                            <Icon icon={icons.Close}/>
                            <span class="sr-only">Close User Info Panel</span>
                        </button>
                    </header>
                    <UserInfo />
                    <button key="page-overlay" type="button" aria-label="Close User Info Panel" class={[
                    'border-0 appearance-none cursor-default',
                    'fixed inset-y-0 right-0 bg-neutral-90/50',
                    'left-94 sm:left-122',
                    'z-(--z-sidebar-panel-backdrop)',
                ]} onClick={this.closePanel}/>
                </aside>);
        },
    },
    methods: {
        /** Close the current panel. */
        closePanel() {
            this.panelOpen = false;
        },
        /** Logout the current user. */
        async logout() {
            await store.UserStore.logout({ revoke: true });
            this.$router.push({ name: 'login' });
        },
    },
    /** @returns The rendered copmponent. */
    render() {
        return (<div>
                <nav class={[
                'fixed left-0 top-0',
                'w-12 h-screen',
                'flex flex-col',
                'text-base',
                /* Color Scheme: Light Mode */
                // control child button hover style here, so we dont have to give complex
                // prominence names for the buttons. (Like a prop that says variant={on-neutral-60})
                'bg-brand-60 text-white',
                '[&_.sidebar-btn]:hover:bg-brand-50',
                '[&_.sidebar-btn]:hover:text-brand-2',
                '[&_.sidebar-btn]:aria-expanded:bg-brand-40',
                '[&_.sidebar-btn.is-active]:bg-brand-50',
                /* Color Scheme: Dark Mode */
                'dark:bg-brand-90 text-brand-10',
                'dark:[&_.sidebar-btn]:hover:bg-brand-80',
                'dark:[&_.sidebar-btn]:hover:text-brand-5',
                'dark:[&_.sidebar-btn]:aria-expanded:bg-brand-60',
                'dark:[&_.sidebar-btn.is-active]:bg-brand-80',
            ]} data-cy="site-sidebar">
                    <header>
                        <SidebarButton type={this.simpleSidebar || this.isStudentViewEnabled
                ? { tag: 'non-interactive' }
                : {
                    tag: 'link',
                    location: { name: 'home' },
                    isActive: this.$route.name === 'home',
                }}>
                            <CgLogo class="w-8 mt-5! mb-3" small inverted={false}/>
                        </SidebarButton>
                    </header>

                    <div class="grid">
                        {utils.ifOrEmpty(this.canLogIn, () => (<SidebarButton icon={icons.LogIn} label="Login" type={{
                    tag: 'link',
                    location: { name: 'login' },
                    isActive: this.$route.name === 'home',
                }}/>))}

                        {utils.ifOrEmpty(this.canRegister, () => (<SidebarButton icon={icons.SignUp} label="Register" type={{
                    tag: 'link',
                    location: { name: 'register' },
                    isActive: this.$route.name === 'home',
                }}/>))}

                        {utils.ifJustOrEmpty(this.loggedInUser, () => (<SidebarButton icon={icons.UserCircle} label="User Profile" sublabel={this.userLabel} buttonAttrs={{
                    'aria-expanded': this.panelOpen,
                }} type={{
                    tag: 'action',
                    action: () => {
                        this.panelOpen = !this.panelOpen;
                    },
                }}/>))}
                    </div>

                    <div class="grow"/>

                    <div class="grid">
                        {utils.ifOrEmpty(this.canManageCurrentLtiAssignment, () => (<SidebarButton icon={icons.OpenInNewTab} label="Open this page in a new tab" type={{
                    tag: 'async-action',
                    loadingMessage: 'Opening new tab',
                    successMessage: '',
                    action: async () => {
                        const session = store.UserStore.dangerousSession();
                        if (session.isNothing()) {
                            throw new Error('No User Session Found.');
                        }
                        const { data: loginFile } = await this.$http.post('/api/v1/files/?max_age=30&ephemeral=true', JSON.stringify(api.UserLoginResponse.encode(session.value)));
                        const curRoute = JSON.stringify({
                            name: this.$route.name,
                            params: this.$route.params,
                            query: this.$route.query,
                            hash: this.$route.hash,
                        });
                        const newRoute = this.$router.resolve({
                            name: 'login_and_redirect',
                            params: {
                                loginFile: loginFile.name,
                            },
                            query: {
                                next: curRoute,
                            },
                        });
                        window.open(newRoute.href, '_blank');
                        return undefined;
                    },
                }}/>))}

                        {utils.ifJustOrEmpty(this.loggedInUser, loggedInUser => (<ToggleStudentViewButton loggedInUser={loggedInUser}/>))}

                        {this.adminPanelButton}

                        <SidebarButton icon={icons.CircleHelp} label="Documentation" type={{ tag: 'external-link', href: getDocumentationLink(this.$route) }}/>
                    </div>

                    <footer class={[
                'bg-brand-50 border-t border-brand-40',
                '[&_.sidebar-btn]:hover:bg-brand-40!',
                'dark:bg-brand-90 border-t border-brand-80',
                'dark:[&_.sidebar-btn]:hover:bg-brand-80!',
            ]}>
                        {utils.ifOrEmpty(this.hasUser && !this.$inLTI, () => (<SidebarButton icon={icons.LogOut} label="Log out" type={{ tag: 'action', action: this.logout }}/>))}
                    </footer>
                </nav>
                {this.panelNode}
            </div>);
    },
});
export default Sidebar;
